import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessagesSquare, Send } from "lucide-react";
import api from "@/lib/api";
import { relTime } from "@/lib/constants";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ThreadPanel({ messageId, onOpenChange, onReplied }) {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);
  const open = Boolean(messageId);

  const load = useCallback(async () => {
    if (!messageId) return;
    try {
      const r = await api.get(`/messages/${messageId}/thread`);
      setThread(r.data);
    } catch (err) {
      console.error("Failed to load thread:", err);
    }
  }, [messageId]);

  useEffect(() => {
    setThread(null);
    load();
    if (!messageId) return;
    const int = setInterval(load, 3000);
    return () => clearInterval(int);
  }, [messageId, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      await api.post(`/messages/${messageId}/thread`, { content: t });
      await load();
      onReplied?.();
    } catch {
      toast.error("Failed to send reply");
    }
  };

  const parent = thread?.parent;
  const replies = thread?.replies || [];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onOpenChange(null)}>
      <SheetContent
        side="right"
        className="p-0 w-full sm:w-[420px] sm:max-w-[420px] border-[#2E3242] bg-[#121318] flex flex-col"
        data-testid="thread-panel"
      >
        <SheetDescription className="sr-only">
          Replies to the selected message.
        </SheetDescription>
        <SheetTitle className="px-4 h-16 flex items-center gap-2 border-b border-[#2E3242] shrink-0 text-gray-100 font-display text-base">
          <MessagesSquare className="w-4 h-4 text-red-400" />
          Thread
          <span className="text-xs font-normal text-gray-500 font-sans">
            {replies.length} {replies.length === 1 ? "reply" : "replies"}
          </span>
        </SheetTitle>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="thread-messages">
          {parent && (
            <div className="rounded-xl border border-red-900/30 bg-[#1E1013]/60 p-3">
              <div className="flex items-center gap-2 mb-1">
                <img src={parent.author_avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                <span className="text-sm font-semibold text-gray-100">{parent.author_name}</span>
                <span className="text-[11px] text-gray-600">{relTime(parent.created_at)}</span>
              </div>
              <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">{parent.content}</p>
            </div>
          )}

          {replies.length === 0 && (
            <p className="text-center text-sm text-gray-600 pt-4">
              No replies yet — start the thread.
            </p>
          )}

          {replies.map((m) => (
            <div key={m.id} className="flex gap-2.5" data-testid={`thread-reply-${m.id}`}>
              <img src={m.author_avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-gray-100">{m.author_name}</span>
                  <span className="text-[11px] text-gray-600">{relTime(m.created_at)}</span>
                </div>
                <p className="text-sm text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                  {m.content}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={submit} className="p-3 border-t border-[#2E3242] shrink-0 flex gap-2">
          <Input
            data-testid="thread-reply-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Reply in thread…"
            className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-10"
          />
          <Button
            data-testid="thread-reply-send"
            type="submit"
            disabled={!text.trim()}
            className="ruby-gradient text-white border border-red-900/40 h-10 px-3"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
